import React from "react";
import { css } from "@emotion/css";

/**
 * The always-visible help strip pinned at the bottom of the session.
 *
 * **Ask** — a quiet entry into the session-level chat thread (with quick
 * "too easy / too hard / how is this relevant?" options).  Hints are a
 * *step-scoped* affordance and live inline under the exercise (see
 * HintCard), so they're not duplicated here.
 *
 * **Wrap up for now** — the honest exit, shown only where it means
 * something (a runtime sitting with a promise still running).  A learner
 * who has to abandon a tab to stop is a learner whose sitting has no
 * ending, and an abandoned session gets no wrap card at all: this ends
 * it properly, with the receipt they earned.
 *
 * Deliberately understated: neutral, secondary controls that stay out of
 * the way.  The activity at the centre is the main show.
 */

const barStyle = css`
  flex-shrink: 0;
  display: flex;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--color-border-subtle);
  background: color-mix(in srgb, var(--color-bg) 90%, transparent);
  backdrop-filter: blur(6px);
`;

const buttonStyle = css`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 9px 14px;
  border-radius: 10px;
  border: 1px solid var(--color-border-subtle);
  background: transparent;
  color: var(--color-text-muted);
  font-size: 0.9em;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: var(--color-border);
    background: var(--color-bg-subtle);
    color: var(--color-text);
  }
`;

const glyphStyle = css`
  font-size: 1em;
  line-height: 1;
  opacity: 0.85;
`;

const barLayoutStyle = css`
  gap: 8px;
`;

/** The exit is quieter than Ask: it should be findable, not inviting. */
const quietButtonStyle = css`
  flex: 0 0 auto;
  color: var(--color-text-muted);
  opacity: 0.85;
`;

interface SessionHelpBarProps {
  onOpenAsk: () => void;
  /** Absent = this sitting has no early exit to offer. */
  onWrapUp?: () => void;
}

export const SessionHelpBar: React.FC<SessionHelpBarProps> = ({ onOpenAsk, onWrapUp }) => {
  return (
    <div className={`${barStyle} ${barLayoutStyle}`} data-testid="session-help-bar">
      <button
        type="button"
        className={buttonStyle}
        onClick={onOpenAsk}
        data-testid="help-ask-button"
      >
        <span className={glyphStyle} aria-hidden="true">💬</span>
        Ask the coach
      </button>
      {onWrapUp && (
        <button
          type="button"
          className={`${buttonStyle} ${quietButtonStyle}`}
          onClick={onWrapUp}
          data-testid="help-wrap-up-button"
        >
          Wrap up for now
        </button>
      )}
    </div>
  );
};
